import { FunctionComponent, useMemo, useState } from 'react';
import {
  AggregateSummary,
  ComplianceDataResponse,
} from '../types/dashboard.types';
import { FaBalanceScale } from 'react-icons/fa';
import RsDateRangeToggle from './DateRangeSelector/RsDateRangeToggle';
import RegulationItem from './RegulationItem';
import RSCard from '../../../ui/containers/RSCard';
import { tailwindColors } from '../../../../../tailwind.config';
import { ProgressSegment } from '../../../ui/AnimatedProgressBar';
import { summariseComplianceDashboardData } from '../dashboard-utils';
import BaseCurrencyAUM from './BaseCurrencyAUM';
import TotalFundsCard from './TotalFundsCard';
import PassRate from './PassRate';
import PeriodCaption from './PeriodCaption';
import LoadingSpinner from '../../../ui/LoadingSpinner';
import dayjs from 'dayjs';
import { RegulationBreakdownData } from '../types/dashboard.types';
import { useIsActiveRangeLoading } from '../utils/date.utils';

interface MancoLevelProps {
  totalsForManco: AggregateSummary;
  dataInDateRange: ComplianceDataResponse;
  onToggleChange: (value: { startDate: string; endDate: string }) => void;
  activeCustomRange?: { startDate: string; endDate: string } | null;
  regulationBreakdown: RegulationBreakdownData;
  entityLabel?: string;
}

function calculateDaysInScope(activeCustomRange: {
  startDate: string;
  endDate: string;
}) {
  return (
    dayjs(activeCustomRange.endDate).diff(
      dayjs(activeCustomRange.startDate),
      'days',
    ) + 1
  );
}

const MancoLevel: FunctionComponent<MancoLevelProps> = ({
  totalsForManco,
  onToggleChange,
  dataInDateRange,
  activeCustomRange,
  regulationBreakdown,
  entityLabel = 'ManCo',
}) => {
  const [daysInScope, setDaysInScope] = useState(1);
  const { Pass, Alert, Fail } = totalsForManco.totalFails;
  const totalChecks = Fail + Alert + Pass;
  const FailPercentage = ((Pass + Alert) / totalChecks) * 100;

  const isActiveRangeLoading = useIsActiveRangeLoading(
    activeCustomRange || null,
    null,
    'dashboard-data-v2',
  );

  const [isDateRangeLoading, setIsDateRangeLoading] = useState(false);

  const segments: ProgressSegment[] = [
    { label: 'Fail', value: Fail, color: tailwindColors.fail },
    { label: 'Alert', value: Alert, color: tailwindColors.alert },
    { label: 'Pass', value: Pass, color: tailwindColors.pass },
  ];

  const totalByRegulatory = useMemo(() => {
    const result = summariseComplianceDashboardData(dataInDateRange);
    return result;
  }, [dataInDateRange]);

  const handleToggleChange = (value: {
    startDate: string;
    endDate: string;
  }) => {
    setDaysInScope(calculateDaysInScope(value));
    onToggleChange?.(value);
  };

  return (
    <RSCard>
      <div className="border-b border-gray-200 pb-2">
        <div className="flex w-full flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <h1 className="text-2xl font-bold tracking-tight text-mainblue">
            {entityLabel} Overview
          </h1>
          <div className="flex items-center">
            <RsDateRangeToggle
              defaultValue={daysInScope}
              options={[
                {
                  label: 'Current',
                  value: 1,
                },
                {
                  label: (
                    <>
                      <span className="block lg:hidden">7d</span>
                      <span className="hidden lg:block">7 Days</span>
                    </>
                  ),
                  value: 7,
                },
                {
                  label: (
                    <>
                      <span className="block lg:hidden">30d</span>
                      <span className="hidden lg:block">30 Days</span>
                    </>
                  ),
                  value: 30,
                },
                {
                  label: (
                    <>
                      <span className="block lg:hidden">90d</span>
                      <span className="hidden lg:block">90 Days</span>
                    </>
                  ),
                  value: 90,
                },
              ]}
              onChange={handleToggleChange}
              activeCustomRange={activeCustomRange}
              regulationBreakdown={regulationBreakdown}
            />
          </div>
        </div>
      </div>

      {/* Content area with overlay when loading */}
      <div className="relative">
        <div className="mt-4">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-stretch">
            {/* AUM and Funds Section */}
            <div className="relative flex-1 rounded-md bg-gray-50 p-4 pt-6">
              <PeriodCaption>By end of chosen period</PeriodCaption>
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-6">
                <BaseCurrencyAUM
                  baseCurrency={dataInDateRange.manco_data.base_currency}
                  aum={totalsForManco.totalAUM}
                  aumList={dataInDateRange.manco_data.time_series ?? []}
                  showChart
                  title="Total AUM"
                />

                <TotalFundsCard
                  totalFunds={totalsForManco.totalFunds}
                  totalReportedFunds={totalsForManco.totalReportedFunds}
                  fundsList={dataInDateRange.manco_data.time_series ?? []}
                />
              </div>
            </div>

            {/* Fail Rate Section */}
            <div className="relative flex-1 rounded-md bg-gray-50 p-4 pt-6">
              <PeriodCaption>Within chosen period</PeriodCaption>
              <PassRate
                percentage={FailPercentage}
                segments={segments}
                totals={totalsForManco}
                showCaption={false}
                className="bg-transparent p-0"
              />
            </div>
          </div>
        </div>

        {/* Pie View Section */}
        <div className="mt-4 rounded-md bg-gray-50">
          <div className="p-4">
            <div className="mb-3 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-gray-500">
              <FaBalanceScale className="text-blue-500" />
              Regulatory Breakdown
            </div>
            <div
              className={`grid gap-4 ${Object.keys(totalByRegulatory || {}).length === 1 ? 'md:grid-cols-1' : 'md:grid-cols-2'}`}
            >
              {totalByRegulatory &&
                (() => {
                  const order = dataInDateRange.regulation_bucket_order ?? [];

                  const sortedEntries = Object.entries(totalByRegulatory).sort(
                    ([idA], [idB]) => {
                      const idxA = order.indexOf(idA);
                      const idxB = order.indexOf(idB);
                      const aListed = idxA !== -1;
                      const bListed = idxB !== -1;

                      if (aListed && bListed) return idxA - idxB;
                      if (aListed && !bListed) return -1;
                      if (!aListed && bListed) return 1;
                      // Keep arrival order for unknowns / missing regulation_bucket_order
                      return 0;
                    },
                  );

                  return sortedEntries.map(([regulationId, regulation]) => {
                    const { statusCounts } = regulation;

                    const totals = {
                      totalFunds: regulation.totalFunds,
                      totalReportedFunds: regulation.totalReportedFunds,
                      totalAUM: regulation.totalAUM,
                      totalFails: statusCounts,
                    };

                    const totalFailChartData = {
                      chartData: [
                        ...(statusCounts.Fail
                          ? [{ name: 'Fail', value: statusCounts.Fail }]
                          : []),
                        ...(statusCounts.Alert
                          ? [{ name: 'Alert', value: statusCounts.Alert }]
                          : []),
                        ...(statusCounts.Pass
                          ? [{ name: 'Pass', value: statusCounts.Pass }]
                          : []),
                      ],
                      failPct:
                        (statusCounts.Fail /
                          (statusCounts.Fail +
                            statusCounts.Alert +
                            statusCounts.Pass)) *
                        100,
                    };

                    return (
                      <RegulationItem
                        key={regulationId}
                        regulation={{
                          id: regulationId,
                          title: regulation.section_title,
                        }}
                        subsectionCounts={regulation.subsectionCounts}
                        totals={totals}
                        totalFailChartData={totalFailChartData}
                        daysInScope={daysInScope}
                        dataInDateRange={dataInDateRange}
                        onToggleChange={handleToggleChange}
                        onLoadingChange={setIsDateRangeLoading}
                        activeCustomRange={activeCustomRange}
                        regulationBreakdown={regulationBreakdown}
                        entityLabel={entityLabel}
                      />
                    );
                  });
                })()}
            </div>
          </div>
        </div>

        {(isActiveRangeLoading || isDateRangeLoading) && (
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/80 backdrop-blur-sm">
            <div className="flex items-center gap-3">
              <LoadingSpinner className="h-6 w-6 border-2" />
              <span className="text-sm text-gray-600">
                Loading {entityLabel} data...
              </span>
            </div>
          </div>
        )}
      </div>
    </RSCard>
  );
};

export default MancoLevel;
